<script setup>
import { computed, toRef, useId } from 'vue'
import { provideFormField } from '@/composables/useFormFieldContext'

const props = defineProps({
  label: { type: String, default: '' },
  hint: { type: String, default: '' },
  error: { type: String, default: '' },
  required: { type: Boolean, default: false },
  /** Explicit control id; auto-generated when omitted. */
  id: { type: String, default: '' },
  density: {
    type: String,
    default: 'default',
    validator: (v) => ['default', 'compact'].includes(v),
  },
})

const autoId = useId()
const controlId = computed(() => props.id || autoId)
const hintId = computed(() => (props.hint ? `${controlId.value}-hint` : ''))
const errorId = computed(() => (props.error ? `${controlId.value}-error` : ''))

const { describedby } = provideFormField({
  id: controlId,
  hintId,
  errorId,
  invalid: computed(() => !!props.error),
  density: toRef(props, 'density'),
  required: toRef(props, 'required'),
})
</script>

<template>
  <div class="form-field" :class="{ 'form-field--compact': density === 'compact' }">
    <label v-if="label" class="form-label" :for="controlId">
      {{ label }}
      <span v-if="required" class="form-required" aria-hidden="true">*</span>
    </label>
    <slot
      :id="controlId"
      :describedby="describedby"
      :invalid="!!error"
      :density="density"
    />
    <p v-if="hint && !error" :id="hintId" class="form-hint">{{ hint }}</p>
    <p v-if="error" :id="errorId" class="form-error" role="alert">{{ error }}</p>
  </div>
</template>
